import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { updateDb } from "../services/updateDb";
import { useAppContext } from "../AppContext";
import MailingContentEditor from "./MailingContentEditor";
import MailingPreview from "./MailingPreview";
import useAutosave, { SaveStatus } from "./useAutosave";
import { Pill, Section, Segmented, StatusPill } from "./mailingUi";

const STAGES = ['7d', '1d', 'expired'];
const TEXTS = ['welcome', ...STAGES];
export const STAGE_TONES = { welcome: 'green', '7d': 'blue', '1d': 'orange', expired: 'red' };

// Every automatic mail that is due right now, one row per environment and mail
export const getDueMails = (audience) => audience.flatMap((env) => [
  ...(env.dueWelcome ? [{ env, kind: 'welcome' }] : []),
  ...(env.dueReminder ? [{ env, kind: env.dueReminder }] : []),
]);

const AutoCard = ({ id, title, intro, enabled, onToggle, children }) => {
  const { t } = useTranslation();
  return (
    <div className="mail-card mail-auto">
      <div className="mail-auto-text">
        <h3 className="mail-section-title mail-auto-title">
          <label htmlFor={id}>{title}</label>
          <Pill tone={enabled ? 'green' : 'gray'}>{enabled ? t('mailing.on') : t('mailing.off')}</Pill>
        </h3>
        <p className="mail-section-sub">{intro}</p>
        <div className="mail-timeline">{children}</div>
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={enabled}
        className={`mail-switch ${enabled ? 'on' : ''}`}
        onClick={onToggle}
      />
    </div>
  );
};

const AutomaticMailsTab = ({ settings, defaults, audience, onSent, active }) => {
  const { t } = useTranslation();
  const { showAlertMessage } = useAppContext();

  const [remindersEnabled, setRemindersEnabled] = useState(settings.trialReminders.enabled);
  const [templates, setTemplates] = useState(settings.trialReminders.templates);
  const [welcomeEnabled, setWelcomeEnabled] = useState(settings.welcome.enabled);
  const [welcomeTemplate, setWelcomeTemplate] = useState(settings.welcome.template);
  const [text, setText] = useState('welcome');
  const [language, setLanguage] = useState('nl');
  const [showConfirm, setShowConfirm] = useState(false);
  const [running, setRunning] = useState(false);

  const due = getDueMails(audience);
  const dueEnvironments = new Set(due.map(({ env }) => env.id)).size;
  // Switches are saved right away, texts while typing
  const autosave = useAutosave(
    { remindersEnabled, templates, welcomeEnabled, welcomeTemplate },
    (value) => ({
      trialReminders: { enabled: value.remindersEnabled, templates: value.templates },
      welcome: { enabled: value.welcomeEnabled, template: value.welcomeTemplate },
    }),
    (value, saved) => (value.remindersEnabled !== saved.remindersEnabled || value.welcomeEnabled !== saved.welcomeEnabled ? 0 : 800),
  );

  const runNow = async () => {
    setRunning(true);
    await autosave.flush();
    const res = await updateDb('/api/mailings/automatic/run', {});
    setRunning(false);
    setShowConfirm(false);
    if (res.success) {
      showAlertMessage('success', t('mailing.automatic_sent', { environments: res.data.environments, sent: res.data.sent, failed: res.data.failed }));
      onSent();
    } else {
      showAlertMessage('error', res.error || t('mailing.error'));
    }
  };

  const isWelcome = text === 'welcome';
  const textContent = isWelcome ? welcomeTemplate : templates[text];
  const setTextContent = (value) => (isWelcome ? setWelcomeTemplate(value) : setTemplates({ ...templates, [text]: value }));

  return (
    <>
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)}>
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>{t('mailing.run_automatic_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('mailing.run_automatic_text', { count: due.length, environments: dueEnvironments })}
        </Modal.Body>
        <Modal.Footer>
          <div className="white-border-btn cancel-btn" onClick={() => setShowConfirm(false)}>{t('cancel')}</div>
          <div style={{ flexGrow: 1 }} />
          <button type="button" className="white-border-btn submit-btn" disabled={running} onClick={runNow}>
            <i className="icon-mail" /> {running ? t('mailing.sending') : t('mailing.send')}
          </button>
        </Modal.Footer>
      </Modal>

      <section className="mail-section">
        <div className="mail-auto-grid">
          <AutoCard
            id="welcomeEnabled"
            title={t('mailing.welcome_enabled')}
            intro={t('mailing.welcome_intro')}
            enabled={welcomeEnabled}
            onToggle={() => setWelcomeEnabled(!welcomeEnabled)}
          >
            <Pill tone="gray" plain>{t('mailing.activated')}</Pill>
            <i className="icon-arrow-right" />
            <Pill tone={STAGE_TONES.welcome} plain>{t('mailing.stage_welcome')}</Pill>
          </AutoCard>
          <AutoCard
            id="remindersEnabled"
            title={t('mailing.reminders_enabled')}
            intro={t('mailing.reminders_intro')}
            enabled={remindersEnabled}
            onToggle={() => setRemindersEnabled(!remindersEnabled)}
          >
            {STAGES.map((s, index) => (
              <React.Fragment key={s}>
                {index > 0 && <i className="icon-arrow-right" />}
                <Pill tone={STAGE_TONES[s]} plain>{t(`mailing.stage_${s}`)}</Pill>
              </React.Fragment>
            ))}
          </AutoCard>
        </div>
        {autosave.status === 'error' && <div className="mail-warning"><SaveStatus status={autosave.status} error={autosave.error} /></div>}
        {!settings.schedulerActive && (
          <div className="mail-warning"><i className="icon-alert" /> <span>{t('mailing.scheduler_inactive')}</span></div>
        )}
      </section>

      <Section
        title={`${t('mailing.due_reminders')} (${due.length})`}
        subtitle={t('mailing.due_sub')}
        actions={(
          <button type="button" className="rq-btn rq-btn-secondary" disabled={due.length === 0} onClick={() => setShowConfirm(true)} style={due.length === 0 ? { opacity: 0.5 } : undefined}>
            <i className="icon-mail" /> {t('mailing.run_reminders_now')}
          </button>
        )}
      >
        {due.length > 0 ? (
          <div className="mail-table-wrap">
            <table className="mail-table">
              <thead>
                <tr>
                  <td>{t('environment')}</td>
                  <td>{t('status')}</td>
                  <td>{t('mailing.mail')}</td>
                  <td>{t('mailing.due_reason')}</td>
                  <td className="num">{t('mailing.admins')}</td>
                  <td>{t('mailing.already_sent')}</td>
                </tr>
              </thead>
              <tbody>
                {due.map(({ env, kind }) => {
                  const date = kind === 'welcome' ? env.activatedAt : env.trialExpires;
                  const sent = [
                    ...(env.welcomeSentAt ? ['welcome'] : []),
                    ...Object.keys(env.remindersSent || {}),
                  ];
                  return (
                    <tr key={`${env.id}-${kind}`}>
                      <td><strong>{env.name}</strong></td>
                      <td><StatusPill status={env.status} /></td>
                      <td><Pill tone={STAGE_TONES[kind]} plain>{t(`mailing.stage_${kind}`)}</Pill></td>
                      <td className="nowrap">
                        {moment(date).format('ll')}
                        <span className="mail-sub">
                          {kind === 'welcome' ? t('mailing.activated') : t('mailing.trial_expires')} · {moment(date).fromNow()}
                        </span>
                      </td>
                      <td className="num">{env.adminCount}</td>
                      <td className="mail-muted">{sent.map((s) => t(`mailing.stage_${s}`)).join(', ') || '–'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mail-empty"><i className="icon-check-circle" />{t('mailing.no_due_reminders')}</div>
        )}
      </Section>

      <Section title={t('mailing.reminder_texts')} subtitle={t('mailing.reminder_texts_sub')} actions={<SaveStatus status={autosave.status} error={autosave.error} />}>
        <div style={{ marginBottom: 18 }}>
          <Segmented
            ariaLabel={t('mailing.mail')}
            value={text}
            onChange={setText}
            options={TEXTS.map((s) => ({ value: s, label: t(`mailing.stage_${s}`) }))}
          />
        </div>
        <div className="mail-compose-grid">
          <MailingContentEditor
            key={text}
            content={textContent}
            onChange={setTextContent}
            language={language}
            onLanguageChange={setLanguage}
            onReset={() => setTextContent(isWelcome ? defaults.welcome : defaults.trialReminders[text])}
          />
          <MailingPreview
            request={isWelcome ? { type: 'welcome', content: welcomeTemplate } : { type: 'trialReminder', stage: text, content: templates[text] }}
            language={language}
            active={active}
          />
        </div>
      </Section>

    </>
  );
};

export default AutomaticMailsTab;
